import { PhoneNumber } from "antd-phone-input"
import { type NamePath, type RuleRender } from "components/Form/Form"

import { AlternateContact } from "interface/user"
import { parseDigits, parsePhoneNumberWithError } from "libphonenumber-js"
import { useCheckPhoneNumber } from "services/users"
import { DEFAULT_COUNTRY } from "utils/constants"

/**
 * `enrollmentId` excludes the enrollment being edited from the uniqueness check,
 * so its own numbers are not reported as already taken.
 */
export function useCheckPhoneNumberAsync(
	userId?: string,
	enrollmentId?: string,
) {
	const { mutateAsync: checkPhoneNumber } = useCheckPhoneNumber(
		userId,
		enrollmentId,
	)
	const ruleCheckPhoneNumberAsync: RuleRender = () => ({
		validator: async (_: any, phoneNumber = "") => {
			if (phoneNumber.length === 0) {
				return Promise.resolve()
			}
			const phoneNumberObj = parsePhoneNumberWithError(phoneNumber, {
				defaultCountry: DEFAULT_COUNTRY,
			})
			if (!phoneNumberObj) {
				return Promise.resolve()
			}
			const digits = parseDigits(phoneNumberObj.nationalNumber)
			if (!digits || digits.length < 3) {
				return Promise.resolve()
			}

			const phoneCheck = await checkPhoneNumber(digits)
			if (!phoneCheck.valid) {
				return Promise.reject(new Error(phoneCheck.message))
			}

			return Promise.resolve()
		},
	})

	return {
		ruleCheckPhoneNumberAsync,
	}
}

export const ruleUniquePhoneNumber =
	(
		phoneNumberFieldPath: NamePath = "phoneNumber",
		alternateContactsFieldPath: NamePath = "alternateContacts",
	): RuleRender =>
	({ getFieldValue }) => ({
		message: "This phone number is already associated with this patient",
		validator: function () {
			const patientPhoneNumber = getFieldValue(phoneNumberFieldPath)
			const alternateContactsValue: AlternateContact[] =
				getFieldValue(alternateContactsFieldPath) ?? []
			const alternatePhoneNumbers = alternateContactsValue
				.filter(({ phoneNumber }) => Boolean(phoneNumber))
				.map(({ phoneNumber }) => phoneNumber.value)

			return isPhoneNumberUnique(alternatePhoneNumbers, patientPhoneNumber)()
		},
	})

export function isPhoneNumberUnique(phoneNumbers: string[], phoneNumber = "") {
	return () => {
		const sanitizedPhoneNumbers = phoneNumbers
			.filter(Boolean)
			.map((phoneNumber) => parseDigits(phoneNumber))
		const sanitizedPhoneNumber = parseDigits(phoneNumber)
		if (sanitizedPhoneNumbers.includes(sanitizedPhoneNumber)) {
			return Promise.reject(new Error())
		}

		return Promise.resolve()
	}
}

export const ruleValidContactInfo =
	(
		phoneNumberFieldPath: NamePath = "phoneNumber",
		supportsTextMessagesFieldPath: NamePath = "supportsTextMessages",
		emailFieldPath: NamePath = "email",
	): RuleRender =>
	({ getFieldValue }) => ({
		validator: function () {
			const phoneNumber = getFieldValue(phoneNumberFieldPath)
			const supportsTextMessages = getFieldValue(supportsTextMessagesFieldPath)
			const email = getFieldValue(emailFieldPath)
			if ((!phoneNumber || !supportsTextMessages) && !email) {
				return Promise.reject(
					new Error(
						"At least one of phone number (with text messages support) or email must be provided",
					),
				)
			}

			return Promise.resolve()
		},
	})

export const normalizePhoneNumber = ({
	countryCode,
	areaCode,
	phoneNumber,
}: PhoneNumber) =>
	`${countryCode ? `+${countryCode}` : ""}${areaCode ?? ""}${phoneNumber ?? ""}`
